import { RoutesConstants } from '@/main/routes/routesConstants';
import { Guid } from 'guid-typescript';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

//Components
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import {
  IAddressInfoState
} from '@/main/components/shared/addressInfo';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import {
  formattedContactSave,
  IContactInfoState
} from '@/main/components/shared/contactInfo';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ModalImages from '@/main/components/shared/modalImages/modalImages';
import MessagePendingAdmin from './messagePendingAdmin';
import ModalCancelQuotation from './modalCancelQuotation';

// Services
import { CustomError } from '@/main/services/class/customError';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import {
  convertStringToDecimal,
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  removeEmptyValues
} from '@/main/services/utils';
import { guidIsValid } from '@/main/services/utilsGuid';
import {
  userIsAdmin,
  userIsBodyshop,
  userIsInsurer,
  userIsSupplier
} from '@/main/services/utilsUser';

// Model
import {
  AddressSaveRequest,
  GlassPreQuoteRequest,
  PreQuoteRequest,
  TirePreQuoteRequest
} from '@/main/models/request';
import useKogControlElementId from '@/main/hooks/useKogControlElementId';
import { Controls } from '@/main/services/constants/constantsControl';
import { currencyToNumber } from '@/main/services/utilsNumber';
import useAsyncModalConfirm from '@/main/components/shared/asyncModalConfirm/asyncModalConfirm';
import { KOGAreaButtons } from '@/main/components/shared/styles';
import { quoteApi } from '@/main/api';
import { useInsurerReducer } from '@/main/store/insurer';

// Local Components/ Styles
import SectionBodyshop from './sectionBodyshop/sectionBodyshop';
import SectionGlass from './sectionGlass/sectionGlass';
import SectionIssues from './sectionIssues/sectionIssues';
import SectionTire from './sectionTire/sectionTire';
import SectionVehicle from './sectionVehicle/sectionVehicle';
import ModalPrequoteSignalR from './modalPrequoteSignalR';
import { IKeysPreQuoteFormGlass, IKeysPreQuoteFormTire, IPreQuoteForm, IPreQuoteFormGlass, IPreQuoteFormTire, PreQuoteProvider, usePreQuoteContext } from './context';
import { backendForFrontendBodyshop, backendForFrontendVehicle, checkHasChangedForm, checkHasPreQuoteFormChange, checkPendingGlass, checkPendingTire, ICheckHasPreQuoteFormChangeResponse } from './utilsPrequote';
import * as S from './styles';
import { signalRClearReceiveUpdatesByKey } from '@/main/store/signalR';
import { SendUpdatesSignalRTypeEnum } from '@/main/services/enum/SendUpdatesSignalRTypeEnum';
import { useDispatch } from 'react-redux';


function PreQuote() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    displayModalCancel,
    preQuoteData,
    bodyshopData,
    loadVehicleManufacurer,
    loadVehicleModel,
    loadSubModel,
    loadVehicleYears,
    isLoadingInfoOrder
  } = usePreQuoteContext();
  const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();

  const {
    setValue,
    getValues,
    trigger,
  } = useFormContext<IPreQuoteForm>();
  const CONTROL_VEHICLE_SECTION = useKogControlElementId(Controls.IdVehicleSession);
  const CONTROL_GLASS_SECTION = useKogControlElementId(Controls.IdGlassSession);
  const CONTROL_TIRE_SECTION = useKogControlElementId(Controls.IdTireSession);
  const CONTROL_BODYSHOP_SECTION = useKogControlElementId(Controls.IdBodyshopSession);
  const CONTROL_DOWNLOAD_IMAGES = useKogControlElementId(Controls.IdDownloadImagesPreQuoteControl);

  const isSupplier = userIsSupplier();
  const [searchParams] = useSearchParams();
  const guidQuote = searchParams.get('guidQuote');

  const hasPendingGlass = checkPendingGlass(preQuoteData);
  const hasPendingTire = checkPendingTire(preQuoteData);

  const [dataContacts, setDataContacts] = useState<IContactInfoState[]>([]);
  const [dataAddress, setDataAddress] = useState<IAddressInfoState[]>([]);
  const [isSavingQuote, setIsSavingQuote] = useState(false);
  const [isLoadingSignalR, setIsLoadingSignalR] = useState<boolean>(false)

  const { insurers: insurerReducer } = useInsurerReducer();

  const formatDataAddress = (
    addresses: IAddressInfoState[],
    isEditAddress = false
  ): AddressSaveRequest[] => {
    const mountAddressRequest = (
      addressDTO: IAddressInfoState
    ): AddressSaveRequest => ({
      ...(isEditAddress && {
        guid: Guid.isGuid(addressDTO.id) ? addressDTO.id : Guid.EMPTY
      }),
      idAddressType: Number(addressDTO.addressType.key),
      street: addressDTO.address,
      complement: addressDTO.complement,
      number: addressDTO.number,
      delivery: addressDTO.isDeliveryAddress,
      municipality: {
        guid: String(addressDTO.municipality.key)
      }
    });

    return addresses.map(address => mountAddressRequest(address));
  };

  function formatData(data: IPreQuoteForm) {
    const formatDataTire = (tire: IPreQuoteFormTire): IPreQuoteFormTire => ({
      ...tire,
      refPrice: currencyToNumber(String(tire.refPrice)) ?? 0
    });
    const formatDataGlass = (glass: IPreQuoteFormGlass): IPreQuoteFormGlass => ({
      ...glass,
      refPrice: currencyToNumber(String(glass.refPrice)) ?? 0
    });


    const result: IPreQuoteForm = {
      ...data,
      tires: data.tires.map(item => formatDataTire(item)),
      glasses: data.glasses.map(item => formatDataGlass(item))
    }

    return result;
  }
  function formatPreQuoteRequest(data: IPreQuoteForm, dataFormChanges: ICheckHasPreQuoteFormChangeResponse): PreQuoteRequest {
    const formattedContacts = formattedContactSave(dataContacts, true)
    const formattedAddress = formatDataAddress(dataAddress, true);
    const isBodyshopAdmin = userIsBodyshop() || userIsAdmin();
    if (isNullUndefined(preQuoteData))
      throw new CustomError(
        getTranslateByKey('information.somethingWrong', true),
        'MISSING_FIELDS'
      );

    const glassRequest: GlassPreQuoteRequest[] = data.glasses.map(glass => ({
      code: glass.code,
      guidQuoteItem: glass.guidQuoteItem,
      priceCap: glass.refPrice
    }));

    const tireRequest: TirePreQuoteRequest[] = data.tires.map(tire => ({
      guidQuoteItem: tire.guidQuoteItem,
      guidBrand: isNullUndefined(tire.brand) ? Guid.EMPTY : String(tire.brand?.key),
      model: tire.model,
      width: convertStringToDecimal(tire.width),
      height: convertStringToDecimal(tire.height),
      rim: tire.rim,
      range: tire.range,
      priceCap: Number(tire.refPrice)
    }));

    const hasChangeRequest: Pick<PreQuoteRequest, 'hasChangedBodyshop' | 'hasChangedGlass' | 'hasChangedTire' | 'hasChangedVehicle'> = {
      hasChangedVehicle: checkHasChangedForm('vehicle', dataFormChanges),
      hasChangedGlass: checkHasChangedForm('glass', dataFormChanges),
      hasChangedTire: checkHasChangedForm('tire', dataFormChanges),
      hasChangedBodyshop: checkHasChangedForm('bodyshop', dataFormChanges) || (formattedAddress.length > 0 && formattedContacts.length > 0),
    }

    const request: PreQuoteRequest = {
      guid: '',
      ...hasChangeRequest,
      vehiclePending: preQuoteData.vehiclePending,
      bodyshopPending: preQuoteData.bodyshopPending,
      glassPending: hasPendingGlass,
      tirePending: hasPendingTire,
      vehicle: {
        vehicleGuidManufacturer: isNullUndefined(data.manufacturer) ? Guid.EMPTY : String(data.manufacturer.key),
        vehicleGuidVehicleModel: isNullUndefined(data.model) ? Guid.EMPTY : String(data.model.key),
        vehicleGuidSubModel: isNullUndefined(data.trim) ? Guid.EMPTY : String(data.trim.key),
        vehicleGuidYear: isNullUndefined(data.year) ? Guid.EMPTY : String(data.year.key)
      },
      glass: glassRequest,
      tire: tireRequest,
      ...(isBodyshopAdmin && {
        bodyShopRequest: {
          guidCountry: isNullUndefined(data.country) ? Guid.EMPTY : String(data.country.key),
          companyName: data?.companyName,
          tradeName: data?.tradeName,
          registrationNumber: data?.rfc,
          idStatusRegister: Number(StatusRegisterEnum.Active),
          guid: String(preQuoteData?.guidBodyShop),
          contacts: formattedContacts,
          addresses: formattedAddress
        }
      })
    }
    return removeEmptyValues(request);
  };
  function savePreQuote(savePreQuoterequest: PreQuoteRequest) {
    savePreQuoterequest.guid = String(guidQuote);
    setIsSavingQuote(true);
    quoteApi
      .savePreQuote(savePreQuoterequest)
      .then(response => { })
      .finally(() => {
        setIsSavingQuote(false);
        setIsLoadingSignalR(true);
      });
  }

  const handleValidationSubmit = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    const data = getValues();
    const formattedData = formatData(data);
    const formChanges = checkHasPreQuoteFormChange({
      dataAPI: preQuoteData,
      dataScreen: formattedData,
      bodyshopData,
      dataAddress,
      dataContacts
    });

    validateForm(formattedData, formChanges);

    const MAP_PENDING_TO_CHANGES = {
      vehicleInformation: {
        hasControl: CONTROL_VEHICLE_SECTION,
        isPending: preQuoteData?.vehiclePending,
        hasChanges: checkHasChangedForm('vehicle', formChanges)
      },
      glassInformation: {
        hasControl: CONTROL_GLASS_SECTION,
        isPending: checkPendingGlass(preQuoteData),
        hasChanges: checkHasChangedForm('glass', formChanges)
      },
      tireInformation: {
        hasControl: CONTROL_TIRE_SECTION,
        isPending: checkPendingTire(preQuoteData),
        hasChanges: checkHasChangedForm('tire', formChanges)
      },
      bodyshop: {
        hasControl: CONTROL_BODYSHOP_SECTION,
        isPending: preQuoteData?.bodyshopPending,
        hasChanges: checkHasChangedForm('bodyshop', formChanges) || (formattedContactSave(dataContacts, true).length > 0 && formatDataAddress(dataAddress, true).length > 0)
      }
    }

    const sectionsPendingNotAnswered: string[] = [];
    for (const [key, value] of Object.entries(MAP_PENDING_TO_CHANGES)) {
      if (value.hasControl && value.isPending && !value.hasChanges) {
        sectionsPendingNotAnswered.push(key)
      }
    }

    const translateSections = sectionsPendingNotAnswered.map(section => getTranslateByKey(`information.${section}`).toLowerCase()).join(', ');
    const translateModal = getTranslateByKey('information.confirmModalPendinSectionsPrequote').replace('{0}', translateSections);

    if (sectionsPendingNotAnswered.length > 0) {
      const hasConfirmModal = await getConfirmation(translateModal)

      if (!hasConfirmModal) return;
    }

    const formattedPreQuoteRequest = formatPreQuoteRequest(formattedData, formChanges);
    dispatch(
      signalRClearReceiveUpdatesByKey(
        SendUpdatesSignalRTypeEnum.PreQuote
      )
    );
    savePreQuote(formattedPreQuoteRequest);
  };

  const [openModalImages, setOpenModalImages] = useState<boolean>(false);

  useEffect(() => {
    if (preQuoteData) {
      const {
        manufacturer,
        model,
        trim,
        year
      } = backendForFrontendVehicle(preQuoteData);

      loadVehicleManufacurer(model, trim, year);
      setValue('manufacturer', manufacturer);

      loadVehicleModel(manufacturer, trim, year);
      setValue('model', model);

      loadSubModel(manufacturer, model, year);
      setValue('trim', trim);

      loadVehicleYears(manufacturer, model, trim);
      setValue('year', year);

      trigger();
    }
  }, [preQuoteData]);

  useEffect(() => {
    if (bodyshopData) {
      const formattedBackend = backendForFrontendBodyshop(bodyshopData)

      setValue('country', formattedBackend.country);
      setValue('companyName', formattedBackend.companyName);
      setValue('rfc', formattedBackend.rfc);
      setValue('tradeName', formattedBackend.tradeName);

      setDataContacts(formattedBackend.contacts);
      setDataAddress(formattedBackend.address);

      trigger();
    }
  }, [bodyshopData]);

  // Handle functions
  function validateForm(data: IPreQuoteForm, formChanges: ICheckHasPreQuoteFormChangeResponse) {
    const messages = [];
    const ignoreFieldsBodyshop = userIsInsurer();

    if (formChanges.hasChange) {
      const hasChangedVehicle = checkHasChangedForm('vehicle', formChanges);
      const hasChangedGlass = checkHasChangedForm('glass', formChanges);
      const hasChangedTire = checkHasChangedForm('tire', formChanges);
      const hasChangedBodyshop = checkHasChangedForm('bodyshop', formChanges);

      if (hasChangedVehicle) {
        if (!data.manufacturer || !guidIsValid(String(data.manufacturer.key)))
          messages.push(
            getTranslateByKey('information.manufacturerRequired', true)
          );
        if (!data.model || !guidIsValid(String(data.model.key)))
          messages.push(getTranslateByKey('information.modelRequired', true));
        if (!data.trim || !guidIsValid(String(data.trim.key)))
          messages.push(getTranslateByKey('information.trimRequired', true));
        if (!data.year || !guidIsValid(String(data.year.key)))
          messages.push(getTranslateByKey('information.yearRequired', true));
      }

      if (hasChangedGlass) {
        const fieldsRequired: IKeysPreQuoteFormGlass[] = ['code'];

        for (const [index, glass] of data.glasses.entries()) {
          for (const field of fieldsRequired) {
            if (!glass[field]) {
              messages.push(getTranslateByKey('information.resolvePendingGlass'));
              break;
            }
          }
          if (messages.length > 0) break;
        }
      }

      if (hasChangedTire) {
        const fieldsRequired: IKeysPreQuoteFormTire[] = ['brand', 'model', 'width', 'height', 'rim', 'range'];

        for (const [index, tire] of data.tires.entries()) {
          for (const field of fieldsRequired) {
            if (!tire[field]) {
              messages.push(getTranslateByKey('information.resolvePendingTire'));
              break;
            }
          }
          if (messages.length > 0) break;
        }
      }

      if (hasChangedBodyshop) {
        if (!ignoreFieldsBodyshop) {
          if (dataContacts.length === 0)
            messages.push(
              getTranslateByKey('information.mustHaveAtLeastOneContact', true)
            );

          if (dataAddress.length === 0)
            messages.push(
              getTranslateByKey('information.mustHaveAtLeastOneAddress', true)
            );

          if (dataContacts.length > 0) {
            const primaryContact = dataContacts.find(
              contact => contact.isPrimaryContact
            );
            if (!primaryContact)
              messages.push(
                getTranslateByKey(
                  'information.mustHaveAtLeastOnePrimaryContact',
                  true
                )
              );
          }

          if (dataAddress.length > 0) {
            const primaryAddress = dataAddress.find(
              address => address.isDeliveryAddress
            );
            if (!primaryAddress)
              messages.push(
                getTranslateByKey(
                  'information.mustHaveAtLeastOneDeliveryAddress',
                  true
                )
              );
          }
        }
      }
    }



    if (messages.length > 0) {
      messages.map(message => toast.error(message));
      throw new CustomError(messages.join('/n'), 'MISSING_FIELDS');
    }
    return true;
  }

  const isAdminApprove =
    userIsInsurer() &&
    !isNullUndefined(preQuoteData) &&
    preQuoteData.bodyshopPending &&
    !preQuoteData.vehiclePending &&
    !checkPendingGlass(preQuoteData) &&
    !checkPendingTire(preQuoteData);

  if (isLoadingInfoOrder) return <LoadingComponent />;
  if (isAdminApprove) return <MessagePendingAdmin />;

  function closeModalImages() {
    setOpenModalImages(false);
  }

  return (
    <form onSubmit={handleValidationSubmit} noValidate>

      <S.Container>
        <SectionIssues setOpenModalImages={setOpenModalImages} />
        <SectionVehicle setOpenModalImages={setOpenModalImages} />
        <SectionGlass />
        <SectionTire />
        <SectionBodyshop
          dataAddress={dataAddress}
          dataContacts={dataContacts}
          setDataAddress={setDataAddress}
          setDataContacts={setDataContacts}
        />

        <KOGAreaButtons>
          <SecondaryButton
            type="button"
            title={getTranslateByKey('information.back')}
            onClick={() => navigate(RoutesConstants.orderSearchOrder)}
            id="id-preQuote-button-back">
            {getTranslateByKey('information.back')}
          </SecondaryButton>
          <SecondaryButton
            type="button"
            title={getTranslateByKey('information.cancelOrder')}
            onClick={() => displayModalCancel()}
            id="id-preQuote-button-cancel">
            {getTranslateByKey('information.cancel')}
          </SecondaryButton>
          <Button
            type="submit"
            title={getTranslateByKey('information.save')}
            isLoading={isSavingQuote}
            id="id-preQuote-button-save">
            {getTranslateByKey('information.save')}
          </Button>
        </KOGAreaButtons>
      </S.Container>

      <ModalImages
        guidClaim=''
        isOpenModal={openModalImages}
        setIsOpenModal={setOpenModalImages}
        closeModal={closeModalImages}
        canUploadImages={!isSupplier}
        canShowPDF={!isSupplier}
        guidQuote={String(guidQuote)}
        canDownloadImages={CONTROL_DOWNLOAD_IMAGES}
      />

      <ModalConfirmation />
      <ModalPrequoteSignalR
        isOpenModal={isLoadingSignalR}
        closeModal={() => setIsLoadingSignalR(false)}
        setIsOpenModal={setIsLoadingSignalR}
      />
    </form>
  );
}

export default function PreQuoteLoaded() {
  const titleScreen = `
  ${getTranslateByKey('information.order')}
  ${' > '}
  ${getTranslateByKey('screen.preQuote')}`;

  return (
    <PreQuoteProvider>
      <ContainerScreen hasBackground title={titleScreen}>
        <PreQuote />
        <ModalCancelQuotation />
      </ContainerScreen>
    </PreQuoteProvider>
  );
}
